// OWNED BY the documents implementation agent — only that agent edits this file
// (plus files it creates under detail/tabs/documents/). Everything else under
// detail/ (registry, tab strip, header, page shell, sessionCache) is frozen
// shared foundation — see docs/crm/customer-page-implementation.md (server repo).
//
// מסמכים tab (mock s19): per-contact file archive. Uploaded docs live in the
// dashboard-users-v2/{email}/contacts/{phone}/documents subcollection (files
// in Firebase Storage, uid-keyed path); the חוזים חתומים group renders
// COMPLETED signature documents read-only from the SHARED
// `signatures:{contactId}` session-cache key — auto-filed, nothing copied.

import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Stack, Button, Divider, Skeleton, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack5";
import Label from "components/Label";
import useFirestore from "hooks/useFirestore";
import { TabCard } from "../TabCard";
import {
  contactDocumentsKey,
  contactSignaturesKey,
  completedSignatureContracts,
  preloadContactDocuments,
  preloadContactSignatureDocuments,
  totalSizeBytes,
  formatFileSize
} from "./documents/documentsData";
import type { ContactDocumentWithId } from "./documents/documentsData";
import { useContactDocuments, useContactSignatureContracts } from "./documents/useContactDocuments";
import { DocumentRow } from "./documents/DocumentRow";
import {
  SignedContractRow,
  SellerQuestionnaireRow,
  QuestionnaireDocumentRow
} from "./documents/SignedContractRow";
import { UploadDropzone } from "./documents/UploadDropzone";
import { getCached, subscribeCached } from "../data/sessionCache";
import { questionnaireGroupTitle } from "utils/signatureQuestionnaireName";
import { isQuestionnaireOnlyDocument } from "../../../../../@types/sharedSchema";
import type { RealEstateContact } from "../../../../../@types/sharedSchema";
import type { SignatureDocument } from "../../../../../@types/signatures";
import type { ContactTabComponentProps, TabBadge } from "./tabContract";

export default function DocumentsTab({ contact }: ContactTabComponentProps): JSX.Element {
  const contactId = contact?.id ?? "";
  const { userSettings } = useFirestore();
  const { enqueueSnackbar } = useSnackbar();
  const hasSignatureFeatureFlag = userSettings?.featureFlagsV2?.includes("signature") ?? false;

  const { documents, error, refresh } = useContactDocuments(contact);
  // Every COMPLETED signature document of the contact. A questionnaire-only document (שאלון בלבד)
  // is filed with the questionnaires and never as a signed contract: its one file IS the filled
  // questionnaire, and a contract row would offer a "signed contract" that is a questionnaire.
  const completed = useContactSignatureContracts(contact, hasSignatureFeatureFlag);
  const contracts = useMemo(
    () => completed.filter((doc) => !isQuestionnaireOnlyDocument(doc)),
    [completed]
  );

  useEffect(() => {
    if (error && documents === undefined) {
      enqueueSnackbar("טעינת המסמכים נכשלה", { variant: "error", autoHideDuration: 4000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const [searchParams, setSearchParams] = useSearchParams();
  // MERGE pattern (never rebuild) — ?contact=, ?tab= and the global
  // new-signature dialog params share one URL.
  const goToSignaturesTab = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "signatures");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const uploaded = useMemo(() => documents ?? [], [documents]);
  const { customerDocs, otherDocs, uploadedContracts } = useMemo(
    () => ({
      customerDocs: uploaded.filter((doc) => doc.category === "customerDoc"),
      otherDocs: uploaded.filter(
        (doc) => doc.category !== "customerDoc" && doc.category !== "signedContract"
      ),
      // Future-proofing: if a signed contract ever gets REALLY filed into the
      // subcollection (e.g. server-side auto-filing), show it with the
      // contracts rather than under "אחר".
      uploadedContracts: uploaded.filter((doc) => doc.category === "signedContract")
    }),
    [uploaded]
  );

  // Questionnaires: the one a signed contract carries (sellerQuestionnaireFile)
  // — surfaced as their own group so realtors find them without opening the
  // signatures tab — and every questionnaire-only document. One row per
  // document, newest first, as `completed` is sorted.
  const questionnaires = useMemo(
    () => completed.filter((doc) => isQuestionnaireOnlyDocument(doc) || !!doc.sellerQuestionnaireFile?.url),
    [completed]
  );

  // Every completed document counts once: a contract (with or without its
  // questionnaire) or a questionnaire-only document — as the tab badge counts.
  const totalCount = uploaded.length + completed.length;
  const sizeLabel = formatFileSize(totalSizeBytes(uploaded));
  const countLabel = totalCount === 1 ? "קובץ אחד" : `${totalCount} קבצים`;
  const contractsCount = contracts.length + uploadedContracts.length;
  // undefined documents = the list was never (successfully) fetched. On a
  // truly cold mount the hook's inflight flag can be false during the whole
  // initial fetch (the entry is created inside its effect), so key the
  // skeleton/empty/failed split off the data + error instead — never present
  // a cold start or a failed load as "אין עדיין מסמכים" (CallsTab convention).
  const showColdSkeleton = documents === undefined && !error;
  const isLoadFailed = documents === undefined && !!error;
  const isEmpty = !showColdSkeleton && !isLoadFailed && totalCount === 0;

  return (
    <TabCard sx={{ width: "100%", maxWidth: 880, mx: "auto" }}>
      <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 1.5 }}>
        <Typography variant="h6">מסמכים וקבצים</Typography>
        {totalCount > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {countLabel}
            {sizeLabel ? ` · ${sizeLabel}` : ""}
          </Typography>
        )}
      </Stack>

      {/* The upload dropzone LEADS the section (design feedback 2026-08) — the
          primary action sits first, the archive lists below it. */}
      <UploadDropzone contactId={contactId} />

      {!isEmpty && <Divider sx={{ my: 2, borderStyle: "dashed" }} />}

      {showColdSkeleton && <DocumentsSkeleton />}

      {isLoadFailed && (
        <Stack alignItems="center" gap={1.5} sx={{ py: 4 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            טעינת המסמכים נכשלה
          </Typography>
          <Button variant="outlined" color="inherit" onClick={refresh}>
            ניסיון חוזר
          </Button>
        </Stack>
      )}

      {!showColdSkeleton && customerDocs.length > 0 && (
        <>
          <GroupHeader title="מסמכי לקוח" count={customerDocs.length} />
          {customerDocs.map((doc) => (
            <DocumentRow key={doc.id} contactId={contactId} doc={doc} />
          ))}
        </>
      )}

      {!showColdSkeleton && otherDocs.length > 0 && (
        <>
          <GroupHeader title="אחר" count={otherDocs.length} />
          {otherDocs.map((doc) => (
            <DocumentRow key={doc.id} contactId={contactId} doc={doc} />
          ))}
        </>
      )}

      {!showColdSkeleton && contractsCount > 0 && (
        <>
          <GroupHeader title="חוזים חתומים" count={contractsCount} color="success" />
          {contracts.map((doc) => (
            <SignedContractRow key={doc.id} doc={doc} />
          ))}
          {uploadedContracts.map((doc) => (
            <DocumentRow key={doc.id} contactId={contactId} doc={doc} />
          ))}
          {hasSignatureFeatureFlag && (
            <Button size="small" onClick={goToSignaturesTab} sx={{ mt: 0.5 }}>
              לניהול תהליכי החתימה ←
            </Button>
          )}
        </>
      )}

      {!showColdSkeleton && questionnaires.length > 0 && (
        <>
          {/* Named by what is in it: a rental's questionnaire is שאלון בעל זכות בנכס. */}
          <GroupHeader
            title={questionnaireGroupTitle(questionnaires.map((doc) => doc.sellerQuestionnaire?.listedFor))}
            count={questionnaires.length}
          />
          {questionnaires.map((doc) =>
            isQuestionnaireOnlyDocument(doc) ? (
              <QuestionnaireDocumentRow key={`q-${doc.id}`} doc={doc} />
            ) : (
              <SellerQuestionnaireRow key={`q-${doc.id}`} doc={doc} />
            )
          )}
        </>
      )}

      {isEmpty && (
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
          אין עדיין מסמכים ללקוח. העלו ת״ז, אישור עקרוני, נסח טאבו או כל קובץ אחר — וחוזים שנחתמים
          דיגיטלית יתויקו לכאן אוטומטית.
        </Typography>
      )}
    </TabCard>
  );
}

type GroupHeaderProps = {
  title: string;
  count: number;
  color?: "default" | "success";
};

function GroupHeader({ title, count, color = "default" }: GroupHeaderProps): JSX.Element {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        mt: 2,
        mb: 0.5,
        pb: 0.5,
        borderBottom: (theme) => `1px dashed ${theme.palette.divider}`
      }}
    >
      <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
        {title}
      </Typography>
      <Label color={color}>{count}</Label>
    </Stack>
  );
}

function DocumentsSkeleton(): JSX.Element {
  return (
    <Stack gap={1.5} sx={{ my: 1.5 }}>
      {[0, 1].map((index) => (
        <Stack key={index} direction="row" alignItems="center" gap={1.5}>
          <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1.5 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton width="40%" height={20} />
            <Skeleton width="60%" height={16} />
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

// Badge for the tab strip: uploaded documents + completed signed contracts.
// Reads ONLY from the session cache (warmed by preloadDocumentsTab / the tab's
// own listeners) — never fetches per render, per the tab contract.
export function useDocumentsTabBadge(contact: RealEstateContact): TabBadge {
  const contactId = contact?.id ?? "";
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!contactId) return undefined;
    const bump = (): void => forceRender((n) => n + 1);
    const unsubscribeDocs = subscribeCached(contactDocumentsKey(contactId), bump);
    const unsubscribeSigs = subscribeCached(contactSignaturesKey(contactId), bump);
    return () => {
      unsubscribeDocs();
      unsubscribeSigs();
    };
  }, [contactId]);

  if (!contactId) return null;

  const docsCount = getCached<ContactDocumentWithId[]>(contactDocumentsKey(contactId))?.length ?? 0;
  const contractsCount = completedSignatureContracts(
    getCached<SignatureDocument[]>(contactSignaturesKey(contactId))
  ).length;
  const count = docsCount + contractsCount;

  return count > 0 ? { count } : null;
}

// Cache warm-up fired on contacts-table row hover and page mount: first page
// of the documents subcollection + the shared signatures key. Idempotent-cheap
// (fetchCached dedups inflight + stale-while-revalidate); never throws.
export function preloadDocumentsTab(contact: RealEstateContact): void {
  try {
    preloadContactDocuments(contact);
    preloadContactSignatureDocuments(contact);
  } catch {
    // preloads are best-effort by contract
  }
}
